import { FC, PointerEvent as ReactPointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { Avatar, Card, Typography } from 'antd';
import { isAxiosError } from 'axios';
import { TbChartInfographic, TbIconsOff } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponents } from '@/hooks/useReportComponents';
import { reportComponentService } from '@/services';
import { ChartContainer, ChartSkeleton, EmptySection } from '@/shared';
import {
    DescriptionReportComponent,
    MultiStatistic,
    ReportTable,
    SingleStatistic,
} from '@/shared/components/data-visualization/ant-design';
import EmbedReportComponent from '@/shared/components/data-visualization/ant-design/99-Embeds/EmbedReportComponent';
import MapReportComponent from '@/shared/components/data-visualization/maplibre/00-Map';
import { Vantage } from '@/shared/components/data-visualization/monaco-editor';
import {
    AreaChart,
    BarChart,
    ChordChart,
    DonutChart,
    FunnelChart,
    GaugeChart,
    HeatMap,
    LineChart,
    PieChart,
    RadarChart,
    RadialChart,
    SankeyChart,
    ScatterPlot,
} from '@/shared/components/data-visualization/nivo';
import { isReportComponentAvailable } from '@/shared/constants';
import { useReportComponentStore } from '@/stores';
import { ReportComponent, ReportComponentType } from '@/types';
import { TitleConfig } from '@/types/api/modules/business-intelligence';
import { centerComponentInMiddleofView } from '@/utilities';

import {
    canResizeReportComponentEmbed,
    getAuthenticatedMemoIdFromLocation,
    getSharedMemoIdFromLocation,
    isPublicReportComponentRoute,
    ReportComponentEmbedFetchMode,
} from './helpers';

import './index.css';

const GRID_COLS = 6;
const ROW_HEIGHT = 24;
const CONTAINER_PADDING_X = 16;
const MARGIN_X = 8;
const HEADER_HEIGHT = 48;
const DEFAULT_EMBED_HEIGHT = 384;
const MIN_EMBED_HEIGHT = 240;
const MAX_EMBED_HEIGHT = 960;

const clampHeight = (height: number) => Math.min(Math.max(height, MIN_EMBED_HEIGHT), MAX_EMBED_HEIGHT);

const calculateAspect = ({
    gridWidth,
    tileHeight,
    tileWidth,
}: {
    gridWidth: number;
    tileHeight: number;
    tileWidth: number;
}) => {
    if (!gridWidth) {
        return { numerator: 1, denominator: 1 };
    }

    const usableWidth = gridWidth - CONTAINER_PADDING_X - MARGIN_X * (GRID_COLS - 1);
    const colWidth = usableWidth / GRID_COLS;
    const chartWidthPx = tileWidth * colWidth;
    const chartHeightPx = tileHeight * ROW_HEIGHT + HEADER_HEIGHT;

    return {
        numerator: chartWidthPx,
        denominator: Math.max(chartHeightPx, 1),
    };
};

const ReadOnlyTitle: FC<{ component: ReportComponent }> = ({ component }) => {
    const { token } = useAntDesignTheme();
    const config = component.configuration as Partial<TitleConfig> | undefined;

    return (
        <Card
            styles={{ body: { padding: 0 } }}
            style={{ height: '100%', width: '100%', borderRadius: token.borderRadius }}
        >
            <div style={{ padding: '1.5rem' }}>
                <Typography.Title level={1} style={{ margin: 0 }}>
                    {config?.text ?? component.name ?? 'Untitled Section'}
                </Typography.Title>
            </div>
        </Card>
    );
};

const ReadOnlyDivider: FC = () => {
    const { token } = useAntDesignTheme();

    return (
        <Card
            styles={{ body: { padding: 0 } }}
            style={{ height: '100%', width: '100%', borderRadius: token.borderRadius }}
        >
            <div style={{ height: '100%', padding: '0 1rem', ...centerComponentInMiddleofView }}>
                <div style={{ width: '100%', borderTop: `1px solid ${token.colorBorder}` }} />
            </div>
        </Card>
    );
};

const UnsupportedEmbeddedReportComponent: FC<{ component?: ReportComponent; description?: string }> = ({
    component,
    description,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <Card
            styles={{ body: { height: '100%', padding: 0 } }}
            style={{ height: '100%', width: '100%', borderRadius: token.borderRadius }}
        >
            <EmptySection
                height='100%'
                icon={<TbIconsOff size='75%' />}
                description={description ?? component?.name ?? 'This embedded component could not be loaded.'}
                label={component?.type ? 'Unsupported Report Component' : 'Report Component Unavailable'}
            />
        </Card>
    );
};

const EmbeddedReportComponentContent: FC<{
    component: ReportComponent;
    executeReadOnlyVantage?: boolean;
    memoId?: string;
    width: number;
}> = ({ component, executeReadOnlyVantage = false, memoId, width }) => {
    const { componentTileConstraints } = useReportComponents();
    const constraints = componentTileConstraints[component.type] ?? {};
    const tileWidth = component.tileWidth ?? constraints.defaultW ?? constraints.minW ?? 6;
    const tileHeight = component.tileHeight ?? constraints.defaultH ?? constraints.minH ?? 12;
    const { denominator, numerator } = useMemo(
        () => calculateAspect({ gridWidth: width, tileHeight, tileWidth }),
        [tileHeight, tileWidth, width],
    );
    const memoCommentResource = useMemo(
        () =>
            memoId
                ? {
                      component: `reportComponent:${component.id}`,
                      metadata: {
                          source: 'memo-report-component-embed',
                          target: {
                              componentId: component.id,
                              componentType: component.type,
                              label: component.name ?? 'Embedded Report Component',
                              nodeType: 'reportComponentEmbed',
                              type: 'component',
                          },
                      },
                      resourceId: memoId,
                      resourceType: 'memos' as const,
                  }
                : undefined,
        [component.id, component.name, component.type, memoId],
    );

    if (!isReportComponentAvailable(component.type)) {
        return null;
    }

    switch (component.type) {
        case ReportComponentType.TITLE:
            return <ReadOnlyTitle component={component} />;
        case ReportComponentType.DESCRIPTION:
            return <DescriptionReportComponent component={component} persistenceMode='readonly' />;
        case ReportComponentType.DIVIDER:
            return <ReadOnlyDivider />;
        case ReportComponentType.COUNTERCYCLICAL_VANTAGE:
            return (
                <Vantage
                    commentResource={memoCommentResource}
                    component={component}
                    executeWhenReadOnly={executeReadOnlyVantage}
                    readOnly
                />
            );
        case ReportComponentType.STATISTIC:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <SingleStatistic component={component} />
                </ChartContainer>
            );
        case ReportComponentType.MULTIPLE_STATISTICS:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <MultiStatistic component={component} />
                </ChartContainer>
            );
        case ReportComponentType.TABLE:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <ReportTable component={component} />
                </ChartContainer>
            );
        case ReportComponentType.MAP:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <MapReportComponent component={component} />
                </ChartContainer>
            );
        case ReportComponentType.FUNNEL_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <FunnelChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.SANKEY_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <SankeyChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.HEAT_MAP:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <HeatMap component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.SCATTER_PLOT:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <ScatterPlot component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.BAR_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <BarChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.GAUGE_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <GaugeChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.DONUT_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <DonutChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.PIE_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <PieChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.AREA_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <AreaChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.LINE_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <LineChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.RADAR_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <RadarChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.RADIAL_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <RadialChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.CHORD_CHART:
            return (
                <ChartContainer commentResource={memoCommentResource} component={component} isVisibleOnHomepage>
                    <ChordChart component={component} numerator={numerator} denominator={denominator} />
                </ChartContainer>
            );
        case ReportComponentType.EMBED:
        case ReportComponentType.YOUTUBE:
        case ReportComponentType.LOOM:
        case ReportComponentType.GOOGLE_CALENDAR:
        case ReportComponentType.MICROSOFT_OUTLOOK_CALENDAR:
        case ReportComponentType.GOOGLE_DRIVE:
        case ReportComponentType.GOOGLE_DOCS:
        case ReportComponentType.GOOGLE_SHEETS:
        case ReportComponentType.MICROSOFT_ONEDRIVE:
        case ReportComponentType.DROPBOX:
        case ReportComponentType.BOX:
        case ReportComponentType.NOTION:
        case ReportComponentType.CONFLUENCE:
        case ReportComponentType.MIRO:
        case ReportComponentType.MICROSOFT_POWER_BI:
        case ReportComponentType.LOOKER:
        case ReportComponentType.TABLEAU:
        case ReportComponentType.ZOHO_ANALYTICS:
            return <EmbedReportComponent commentResource={memoCommentResource} component={component} />;
        case ReportComponentType.VIMEO:
        case ReportComponentType.GITHUB:
        case ReportComponentType.HEX:
        case ReportComponentType.LUCID:
        case ReportComponentType.GRAPHY:
            return (
                <EmbedReportComponent
                    commentResource={memoCommentResource}
                    component={component}
                    transformScale='1.0'
                />
            );
        default:
            return <UnsupportedEmbeddedReportComponent component={component} />;
    }
};

interface MemoReportComponentEmbedFrameProps {
    editable?: boolean;
    fetchMode?: ReportComponentEmbedFetchMode;
    height?: number;
    memoId?: string;
    onHeightChange?: (height: number) => void;
    reportComponentId?: string;
}

export const MemoReportComponentEmbedFrame: FC<MemoReportComponentEmbedFrameProps> = ({
    editable = false,
    fetchMode = 'authenticated',
    height: initialHeight,
    memoId,
    onHeightChange,
    reportComponentId,
}) => {
    const { token } = useAntDesignTheme();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const [component, setComponent] = useState<ReportComponent | undefined>(() =>
        reportComponentId && fetchMode === 'authenticated'
            ? useReportComponentStore.getState().reportComponentsById[reportComponentId]
            : undefined,
    );
    const hasConfiguredHeight = Number.isFinite(initialHeight);
    const [loading, setLoading] = useState(Boolean(reportComponentId && !component));
    const [error, setError] = useState<string | undefined>();
    const { componentTileConstraints } = useReportComponents();
    const constraints = component ? componentTileConstraints[component.type] : undefined;
    const tileWidth = component?.tileWidth ?? constraints?.defaultW ?? constraints?.minW ?? 6;
    const tileHeight = component?.tileHeight ?? constraints?.defaultH ?? constraints?.minH ?? 12;
    const defaultHeight = useMemo(() => {
        if (!width) {
            return clampHeight(tileHeight * ROW_HEIGHT + HEADER_HEIGHT);
        }

        const { denominator, numerator } = calculateAspect({ gridWidth: width, tileHeight, tileWidth });

        return clampHeight(width * (denominator / Math.max(numerator, 1)));
    }, [tileHeight, tileWidth, width]);
    const [height, setHeight] = useState(clampHeight(initialHeight ?? DEFAULT_EMBED_HEIGHT));

    useEffect(() => {
        if (hasConfiguredHeight) {
            setHeight(clampHeight(initialHeight as number));
            return;
        }

        setHeight(defaultHeight);
    }, [defaultHeight, hasConfiguredHeight, initialHeight]);

    useEffect(() => {
        const element = wrapperRef.current;

        if (!element) {
            return;
        }

        const resizeObserver = new ResizeObserver(([entry]) => {
            setWidth(entry.contentRect.width);
        });

        resizeObserver.observe(element);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!reportComponentId) {
            setError('Missing report component id.');
            return;
        }

        let cancelled = false;

        const loadReportComponent = async () => {
            const cached =
                fetchMode === 'authenticated'
                    ? useReportComponentStore.getState().reportComponentsById[reportComponentId]
                    : undefined;

            if (cached) {
                setComponent(cached);
            }

            setLoading(!cached);
            setError(undefined);

            try {
                const nextComponent =
                    fetchMode === 'public-memo' && memoId
                        ? await reportComponentService.getPublicMemoComponent(memoId, reportComponentId)
                        : fetchMode === 'public-component'
                          ? await reportComponentService.getPublicById(reportComponentId)
                          : await reportComponentService.getById(reportComponentId);

                if (!cancelled) {
                    setComponent(nextComponent);

                    if (fetchMode === 'authenticated') {
                        useReportComponentStore.setState((state) => ({
                            reportComponentsById: {
                                ...state.reportComponentsById,
                                [nextComponent.id]: nextComponent,
                            },
                        }));
                    }
                }
            } catch (loadError: unknown) {
                if (!cancelled) {
                    const message = isAxiosError<{ message?: string }>(loadError)
                        ? loadError.response?.data.message
                        : undefined;
                    setError(message ?? 'Unable to load embedded report component.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadReportComponent();

        return () => {
            cancelled = true;
        };
    }, [fetchMode, memoId, reportComponentId]);

    const startResize = (event: ReactPointerEvent<HTMLDivElement>, edge: 'top' | 'bottom') => {
        if (!editable) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const startY = event.clientY;
        const startHeight = height;

        const handlePointerMove = (moveEvent: PointerEvent) => {
            const delta = moveEvent.clientY - startY;
            const nextHeight = clampHeight(edge === 'bottom' ? startHeight + delta : startHeight - delta);

            setHeight(nextHeight);
            onHeightChange?.(nextHeight);
        };

        const handlePointerUp = () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    };

    const resizeHandle = (edge: 'top' | 'bottom') =>
        editable ? (
            <div
                className={`memo-report-component-embed-resize-handle memo-report-component-embed-resize-handle-${edge}`}
                onPointerDown={(event) => startResize(event, edge)}
            />
        ) : null;

    return (
        <div
            ref={wrapperRef}
            data-memo-report-component-id={reportComponentId}
            data-memo-report-component-type={component?.type}
            data-memo-toc-title={component?.name ?? component?.type ?? 'Report Component'}
            className='memo-report-component-embed'
            style={{
                borderRadius: token.borderRadius,
                background: token.colorBgContainer,
            }}
        >
            {resizeHandle('top')}
            <div style={{ height, overflow: 'auto' }}>
                {loading ? (
                    <Card
                        styles={{
                            body: {
                                height: '100%',
                                padding: '3.5rem 0.75rem 1rem',
                            },
                        }}
                        style={{ height: '100%', width: '100%', borderRadius: token.borderRadius }}
                    >
                        <ChartSkeleton />
                    </Card>
                ) : error ? (
                    <UnsupportedEmbeddedReportComponent description={error} />
                ) : component ? (
                    <EmbeddedReportComponentContent
                        component={component}
                        executeReadOnlyVantage={fetchMode === 'authenticated'}
                        memoId={fetchMode === 'authenticated' ? memoId : undefined}
                        width={width}
                    />
                ) : (
                    <Card
                        styles={{ body: { padding: 0 } }}
                        style={{ height: '100%', width: '100%', borderRadius: token.borderRadius }}
                    >
                        <div style={{ height: '100%', textAlign: 'center', ...centerComponentInMiddleofView }}>
                            <Avatar
                                size={40}
                                shape='square'
                                icon={<TbChartInfographic size='75%' style={{ color: token.colorTextTertiary }} />}
                                style={{ border: `1px solid ${token.colorBorder}`, marginBottom: '0.5rem' }}
                            />
                            <Typography.Text>Embedded Report Component</Typography.Text>
                        </div>
                    </Card>
                )}
            </div>
            {resizeHandle('bottom')}
        </div>
    );
};

export const ReactReportComponentEmbed: FC<NodeViewProps> = ({ editor, node, updateAttributes }) => {
    const reportComponentId = node.attrs.reportComponentId as string | undefined;
    const rawHeight = node.attrs.height;
    const parsedHeight = typeof rawHeight === 'number' ? rawHeight : rawHeight ? Number(rawHeight) : undefined;
    const height = Number.isFinite(parsedHeight) ? parsedHeight : undefined;
    const publicMemoId = getSharedMemoIdFromLocation();
    const authenticatedMemoId = getAuthenticatedMemoIdFromLocation();
    const memoId = publicMemoId ?? authenticatedMemoId;
    const fetchMode: ReportComponentEmbedFetchMode = publicMemoId
        ? 'public-memo'
        : isPublicReportComponentRoute()
          ? 'public-component'
          : 'authenticated';

    return (
        <NodeViewWrapper contentEditable={false}>
            <MemoReportComponentEmbedFrame
                editable={canResizeReportComponentEmbed(editor.isEditable, fetchMode)}
                fetchMode={fetchMode}
                height={height}
                memoId={memoId}
                reportComponentId={reportComponentId}
                onHeightChange={(nextHeight) => updateAttributes({ height: nextHeight })}
            />
        </NodeViewWrapper>
    );
};
